import { Link, useSearchParams } from "react-router-dom";

import { usePoll } from "../api";
import type { Incident } from "../api";
import { dayKey, fmtDay, fmtDuration, fmtTime } from "../format";

/** The disturbance diary: incidents, newest first, grouped by UK day. */
export function Diary() {
  const [params] = useSearchParams();
  const device = params.get("device");
  const path = `/api/incidents${device ? `?device_id=${encodeURIComponent(device)}` : ""}`;
  const { data, error } = usePoll<Incident[]>(path, 15_000);

  if (error) return <p className="error">{error}</p>;
  if (!data) return <div className="spinner" />;

  if (data.length === 0) {
    return (
      <section>
        <h2>Diary</h2>
        <div className="card empty">
          <p>Nothing logged yet.</p>
          <p className="muted">
            Once your device has learned the sound, every time it happens appears here — when it
            started, how long it went on, and a recording.
          </p>
          <Link to="/devices">Go to your device →</Link>
        </div>
      </section>
    );
  }

  const days = new Map<string, Incident[]>();
  for (const i of data) {
    const key = dayKey(i.started_at);
    days.set(key, [...(days.get(key) ?? []), i]);
  }

  return (
    <section>
      <h2>Diary</h2>
      {[...days.entries()].map(([key, items]) => (
        <div key={key} className="day">
          <h3>
            {fmtDay(items[0].started_at)}
            <span className="muted small">
              {" "}· {items.length} incident{items.length === 1 ? "" : "s"}
            </span>
          </h3>
          <ul className="list">
            {items.map((i) => (
              <li key={i.id}>
                <Link to={`/incidents/${i.id}`} className={`card row ${i.isolated ? "faint" : ""}`}>
                  <span className="when">
                    {fmtTime(i.started_at)}–{i.status === "open" ? "now" : fmtTime(i.last_detection_at)}
                  </span>
                  <span className="grow">
                    {fmtDuration(i.started_at, i.last_detection_at)} · {i.detection_count} bark
                    {i.detection_count === 1 ? "" : "s"}
                  </span>
                  {i.status === "open" && <span className="pill live">Happening</span>}
                  {i.verification === "unverified" && <span className="pill warn">Unverified</span>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
